import { useEffectWithController } from '@carvana/merch-react-hooks';
import { useContext, useEffect, useMemo, useRef } from 'react';
import { axios, httpWithAuth } from '~/clients';
import { PRICING } from '~/constants/endpoints';
import AppliedFiltersContext from '~/context/common/AppliedFiltersContext';
import TradeContext, {
  useGetAcquisitionNameForApiRequest,
  useIsTradeInApplied
} from '~/context/common/TradeContext';
import { useIsLoggedIn, useUserLocation } from '~/context/common/UserContext';
import ErrorContext from '~/context/srp/ErrorContext';
import FinanceContext, {
  formatFinanceInfo,
  useShouldRefreshSoftPull
} from '~/context/srp/FinanceContext';
import InventoryContext from '~/context/srp/InventoryContext';
import PricingContext, {
  useShouldUpdateTerms
} from '~/context/srp/PricingContext';
import SearchContext from '~/context/srp/SearchContext';
import { useVerboseLogging } from '~/context/srp/StaticContext';
import useGenerateAnalyticsData from '~/hooks/srp/useGenerateAnalyticsData';
import useHandleTradeOnPricingFailure from '~/hooks/srp/useHandleTradeOnPricingFailure';
import useResetInventoryPricing from '~/hooks/srp/useResetInventoryPricing';
import type { PricingResponse } from '~/services/common/contracts';
import { isDefined } from '~/utilities/array';
import Cookies from '~/utilities/BrowserCookies';
import { generateErrorLogger, logError } from '~/utilities/loggers';
import useSendPriceUpdateEvent from './useSendPriceUpdateEvent';

export type ChangeLog = {
  softpullRefresh: boolean;
  tradeToggled: boolean;
  initialCall: boolean;
};

function useShouldFetchPricing(): {
  shouldFetch: boolean;
  changeLog: ChangeLog;
} {
  const { zip } = useUserLocation();
  const shouldRefreshSoftPull = useShouldRefreshSoftPull();

  const {
    inventory: {
      data: {
        inventory: { vehicles }
      }
    }
  } = useContext(InventoryContext);

  const tradeToggled = useShouldUpdateTerms();

  const vehiclesNeedFinanceData = useMemo(
    () =>
      // This check handles three cases: 1) LoanTermPricing not being called during
      // SSR, and 2) LoanTermPricing (SSR or CSR) being called but failing
      vehicles.length > 0 &&
      vehicles.every(
        vehicle =>
          !vehicle.price?.downPayment &&
          !vehicle.price?.monthlyPayment &&
          !vehicle.price?.pricingErrorMessage
      ),
    [vehicles]
  );

  return useMemo(
    () => ({
      shouldFetch:
        shouldRefreshSoftPull ||
        tradeToggled ||
        Boolean(zip && vehiclesNeedFinanceData),
      changeLog: {
        softpullRefresh: shouldRefreshSoftPull,
        tradeToggled: tradeToggled,
        initialCall: Boolean(zip && vehiclesNeedFinanceData)
      }
    }),
    [shouldRefreshSoftPull, tradeToggled, vehiclesNeedFinanceData, zip]
  );
}

function useGetPricing() {
  const {
    appliedFilters: { finance }
  } = useContext(AppliedFiltersContext);
  const { updateResponseErrors } = useContext(ErrorContext);
  const { setFinanceInfo, setIsFinanceInitialized, setShouldRefreshSoftPull } =
    useContext(FinanceContext);
  const {
    inventory: {
      data: {
        inventory: { vehicles }
      }
    },
    updateInventoryPricing
  } = useContext(InventoryContext);
  const { setShouldUpdateTerms } = useContext(PricingContext);
  const {
    tradeInfoFromApis: { setData: setTradeInfo }
  } = useContext(SearchContext);
  const { setTradeInApplied } = useContext(TradeContext);

  const { downPayment, monthlyPayment } = finance ?? {};
  const isTradeInApplied = useIsTradeInApplied();
  const isLoggedIn = useIsLoggedIn();
  const { zip } = useUserLocation();
  const generateAnalyticsData = useGenerateAnalyticsData();
  const verboseLoggingEnabled = useVerboseLogging();
  const { shouldFetch: shouldFetchPricing, changeLog } =
    useShouldFetchPricing();
  const priceUpdateEvent = useSendPriceUpdateEvent({ changeLog });
  const preferredAcquisitionName = useGetAcquisitionNameForApiRequest();
  const handleTradeDataOnFailure = useHandleTradeOnPricingFailure();

  const vehicleIds = useMemo(
    () => vehicles.map(vehicle => vehicle.vehicleId),
    [vehicles]
  );

  // Use refs here because we don't want to trigger new API requests when
  // vehicle IDs change.
  const vehicleIdsRef = useRef(vehicleIds);
  useEffect(() => {
    vehicleIdsRef.current = vehicleIds;
  }, [vehicleIds]);

  useResetInventoryPricing(vehicleIds);

  useEffectWithController(
    controller => {
      if (!shouldFetchPricing) return;

      const client = isLoggedIn ? httpWithAuth : axios;
      const shouldSendFinanceFilters = downPayment || monthlyPayment;
      const financeFilters = shouldSendFinanceFilters
        ? { downPayment, monthlyPayment }
        : undefined;

      client
        .post<PricingResponse>(
          PRICING,
          {
            applyVerboseLogging: verboseLoggingEnabled,
            browserCookieId: Cookies.get('BrowserCookieId'),
            vehicleIds: vehicleIdsRef.current,
            zip5: zip,
            financeFilters,
            analyticsData: generateAnalyticsData(),
            applyTradeIn: isTradeInApplied,
            preferredAcquisitionName
          },
          { signal: controller.signal }
        )
        .then(({ data }) => {
          const noVehiclesWithTerms = Object.values(
            data.vehiclePaymentTermsMapping
          ).every(
            vehicle =>
              !isDefined(vehicle.downPayment) &&
              !isDefined(vehicle.monthlyPayment)
          );

          if (data.financeInfo.pricingHasErrored || noVehiclesWithTerms) {
            const errorMessage = 'Pricing has errored';
            logError(new Error(errorMessage), { location: 'Pricing' });
            updateResponseErrors({ pricingError: errorMessage });
          } else {
            setFinanceInfo(formatFinanceInfo(data.financeInfo));
            setIsFinanceInitialized(true);
            setShouldRefreshSoftPull(false);
            updateResponseErrors({ pricingError: null });
            priceUpdateEvent(data);
          }
          updateInventoryPricing(data);

          if (data.tradeInfo) {
            setTradeInfo(data.tradeInfo);
            const isActive =
              data.tradeInfo.status === 'active' &&
              data.tradeInfo.totalTradeInEquity !== 0 &&
              data.tradeInfo.totalTradeInValue !== 0;

            if (isActive && data.tradeInfo.applyTradeIn != null) {
              setTradeInApplied(data.tradeInfo.applyTradeIn);
            }
          }
        })
        .catch(
          generateErrorLogger(
            'Pricing',
            {},
            {
              callback: () => {
                handleTradeDataOnFailure(controller);
                updateResponseErrors({
                  pricingError: 'Failed to fetch pricing.'
                });
              },
              controller
            }
          )
        )
        .finally(() => setShouldUpdateTerms(false));
    },
    [
      downPayment,
      generateAnalyticsData,
      handleTradeDataOnFailure,
      isLoggedIn,
      isTradeInApplied,
      monthlyPayment,
      preferredAcquisitionName,
      priceUpdateEvent,
      setFinanceInfo,
      setIsFinanceInitialized,
      setShouldRefreshSoftPull,
      setShouldUpdateTerms,
      setTradeInApplied,
      setTradeInfo,
      shouldFetchPricing,
      updateInventoryPricing,
      updateResponseErrors,
      verboseLoggingEnabled,
      zip
    ]
  );
}

export default useGetPricing;
